/**
 * Header behaviour: desktop nav indicator, scroll state, mobile drawer.
 *
 * Three small custom elements rather than one, so each surface fails
 * independently — a problem in the drawer can't take the desktop nav with it.
 */

/**
 * <burnout-nav>
 *
 * Slides a single underline between nav items instead of giving each link its
 * own. It follows the hovered item and settles back on the current page when
 * the pointer leaves, which reads as one continuous object rather than a row
 * of separate effects.
 *
 * Position is written as CSS custom properties and animated in CSS, so the
 * motion still honours prefers-reduced-motion without any JS branching.
 */
class BurnoutNav extends HTMLElement {
  #indicator = null;
  #links = [];
  #currentLink = null;

  connectedCallback() {
    this.#indicator = this.querySelector('.bo-nav__indicator');
    this.#links = Array.from(this.querySelectorAll('.bo-nav__link'));
    if (!this.#indicator || !this.#links.length) return;

    this.#currentLink = this.querySelector('.bo-nav__link.is-current');

    this.#links.forEach((link) => {
      link.addEventListener('pointerenter', () => this.#moveTo(link));
      link.addEventListener('focus', () => this.#moveTo(link));
    });

    this.addEventListener('pointerleave', this.#reset);
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.#reset();
    });

    // Fonts load after first paint and change link widths, so re-measure once
    // they're ready rather than baking in the fallback-font geometry.
    document.fonts?.ready.then(this.#reset);
    window.addEventListener('resize', this.#reset);

    this.#reset();
  }

  #moveTo = (link) => {
    if (!link) return;
    const bounds = link.getBoundingClientRect();
    const origin = this.getBoundingClientRect();
    this.#indicator.style.setProperty('--bo-x', `${bounds.left - origin.left}px`);
    this.#indicator.style.setProperty('--bo-w', `${bounds.width}px`);
    this.#indicator.dataset.visible = 'true';
  };

  #reset = () => {
    if (this.#currentLink) {
      this.#moveTo(this.#currentLink);
    } else if (this.#indicator) {
      // No current page in this menu — hide rather than parking it arbitrarily.
      this.#indicator.dataset.visible = 'false';
    }
  };
}

/**
 * Header scroll state.
 *
 * Flags the document once the page has scrolled, so the header can compact and
 * pick up a blurred backdrop. Bound to the document rather than to a wrapper
 * element, so Horizon's own header markup needs no changes.
 *
 * rAF-throttled, with asymmetric thresholds: it takes 64px to compact but the
 * state only releases below 24px, so the header never flickers when someone
 * hovers around the boundary.
 */
function initHeaderScrollState() {
  let ticking = false;
  let scrolled = false;

  const update = () => {
    const next = scrolled ? window.scrollY > 24 : window.scrollY > 64;
    if (next === scrolled) return;
    scrolled = next;
    document.documentElement.classList.toggle('bo-header-scrolled', next);
  };

  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        update();
        ticking = false;
      });
    },
    { passive: true }
  );

  update();
}

initHeaderScrollState();

/**
 * <burnout-drawer>
 *
 * Wraps a native <dialog>, which gives focus trapping, Esc to close and
 * inertness of the page behind it for free.
 *
 * Closing is deferred until the panel's transition has finished so the exit
 * animation is actually visible — `close()` on its own removes the dialog
 * from the box tree instantly.
 */
class BurnoutDrawer extends HTMLElement {
  #dialog = null;
  #panel = null;
  #closing = false;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    this.#panel = this.querySelector('.bo-drawer__panel');
    if (!this.#dialog) return;

    const id = this.#dialog.id;
    document
      .querySelectorAll(`[data-bo-drawer-open="${id}"]`)
      .forEach((trigger) => trigger.addEventListener('click', this.open));

    this.querySelectorAll('[data-bo-drawer-close]').forEach((trigger) =>
      trigger.addEventListener('click', this.close)
    );

    // Clicking the dimmed area outside the panel closes it.
    this.#dialog.addEventListener('click', (event) => {
      if (event.target === this.#dialog) this.close();
    });

    // Esc fires `cancel`; intercept so it plays the exit animation too.
    this.#dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });

    this.#initSwipeToClose();
  }

  /**
   * Drag the panel left to dismiss.
   *
   * The panel tracks the finger rather than waiting for a gesture to complete,
   * so the drawer feels attached to the touch instead of interpreting it. Past
   * a third of its width — or on a quick flick — it closes; otherwise it
   * springs back.
   */
  #initSwipeToClose() {
    if (!this.#panel) return;

    let startX = null;
    let startedAt = 0;
    let delta = 0;

    this.#panel.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType === 'mouse') return;
        startX = event.clientX;
        startedAt = performance.now();
        delta = 0;
        this.#panel.classList.add('is-dragging');
      },
      { passive: true }
    );

    this.#panel.addEventListener(
      'pointermove',
      (event) => {
        if (startX === null) return;
        // Leftward only; dragging right shouldn't peel it off the edge.
        delta = Math.min(0, event.clientX - startX);
        this.#panel.style.transform = `translateX(${delta}px)`;
      },
      { passive: true }
    );

    const release = () => {
      if (startX === null) return;
      const travelled = Math.abs(delta);
      const velocity = travelled / Math.max(1, performance.now() - startedAt);
      this.#panel.classList.remove('is-dragging');
      this.#panel.style.transform = '';
      startX = null;

      if (travelled > this.#panel.offsetWidth / 3 || velocity > 0.5) this.close();
    };

    this.#panel.addEventListener('pointerup', release, { passive: true });
    this.#panel.addEventListener('pointercancel', release, { passive: true });
  }

  open = () => {
    if (!this.#dialog || this.#dialog.open) return;
    this.#closing = false;
    this.#dialog.showModal();
    document.body.classList.add('bo-drawer-open');
    // Commit the closed state with a forced reflow before flipping to open, so
    // the transition has somewhere to animate from. Deliberately not
    // requestAnimationFrame: frame callbacks are throttled in background tabs,
    // which would leave a modal drawer open but invisible — trapping the user.
    void this.#dialog.offsetHeight;
    this.#dialog.classList.add('is-open');
  };

  close = () => {
    if (!this.#dialog || !this.#dialog.open || this.#closing) return;
    this.#closing = true;
    this.#dialog.classList.remove('is-open');
    document.body.classList.remove('bo-drawer-open');

    const finish = () => {
      this.#dialog.close();
      this.#closing = false;
    };

    // Wait for the panel to slide out — but never hang if the transition
    // doesn't run (reduced motion, or the tab being backgrounded).
    const motionOk = window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
    if (!motionOk || !this.#panel) return finish();

    let done = false;
    const once = () => {
      if (done) return;
      done = true;
      finish();
    };
    this.#panel.addEventListener('transitionend', once, { once: true });
    setTimeout(once, 500);
  };
}

if (!customElements.get('burnout-nav')) customElements.define('burnout-nav', BurnoutNav);
if (!customElements.get('burnout-drawer')) customElements.define('burnout-drawer', BurnoutDrawer);
